// Diagnostic translation between LSP and the two consumer surfaces:
//
//   1. CodeMirror's `@codemirror/lint` `Diagnostic` (used for gutter
//      squigglies + the lint panel). Needs absolute offsets and CM's
//      severity vocabulary.
//   2. `@nowline/browser`'s `DiagnosticRow` (used by `@nowline/preview-shell`'s
//      diagnostic table). Needs 1-based line / column and a synthetic
//      file path.
//
// Both shapes share the same upstream — `Diagnostic` from
// `vscode-languageserver-protocol`'s `publishDiagnostics`. Keeping the
// conversions in one module means downstream filters (e.g. severity
// thresholds, code rewrites) stay aligned across the editor and
// preview surfaces.

import type { Diagnostic as CmDiagnostic } from '@codemirror/lint';
import { openLintPanel } from '@codemirror/lint';
import type { Text } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';
import type { DiagnosticRow } from '@nowline/browser';
import {
    DiagnosticSeverity,
    type Diagnostic as LspDiagnostic,
} from 'vscode-languageserver-protocol';
import { lspRangeToCm } from './positions.ts';

const DID_YOU_MEAN_RE = /did you mean ['"]?([^'"?]+)['"]?\??/i;

function diagnosticMessageText(message: LspDiagnostic['message']): string {
    if (typeof message === 'string') return message;
    return message.value;
}

export function lspToCmDiagnostic(diag: LspDiagnostic, doc: Text): CmDiagnostic {
    const message = diagnosticMessageText(diag.message);
    const { from, to } = lspRangeToCm(doc, diag.range);
    return {
        from,
        // CodeMirror collapses zero-width diagnostics; widen by one
        // character so single-token errors still get a visible squiggle.
        to: from === to ? Math.min(doc.length, from + 1) : to,
        severity: lspSeverityToCm(diag.severity),
        message,
        source: codeToString(diag.code),
        // "View Problem" opens the bottom lint panel where the user can
        // navigate diagnostics with the prev/next buttons (or F8/Shift-F8).
        actions: [
            {
                name: 'View Problem',
                apply(view: EditorView) {
                    openLintPanel(view);
                },
            },
        ],
    };
}

export function lspToDiagnosticRow(diag: LspDiagnostic, file: string): DiagnosticRow {
    const message = diagnosticMessageText(diag.message);
    // The preview-shell table treats `severity === 'error'` as red and
    // everything else as amber; LSP's Information / Hint collapse to
    // 'warning' here to match the table's two-state colouring.
    const severity: DiagnosticRow['severity'] =
        diag.severity === DiagnosticSeverity.Error ? 'error' : 'warning';
    const row: DiagnosticRow = {
        severity,
        code: codeToString(diag.code) || inferCodeFromMessage(message),
        message,
        file,
        line: diag.range.start.line + 1,
        column: diag.range.start.character + 1,
    };
    const suggestion = extractSuggestion(message);
    if (suggestion) row.suggestion = suggestion;
    return row;
}

function lspSeverityToCm(severity: LspDiagnostic['severity']): CmDiagnostic['severity'] {
    switch (severity) {
        case DiagnosticSeverity.Error:
            return 'error';
        case DiagnosticSeverity.Warning:
            return 'warning';
        case DiagnosticSeverity.Information:
            return 'info';
        case DiagnosticSeverity.Hint:
            return 'hint';
        default:
            return 'error';
    }
}

function codeToString(code: LspDiagnostic['code']): string {
    if (typeof code === 'string') return code;
    if (typeof code === 'number') return String(code);
    return '';
}

// Mirrors `@nowline/browser`'s `fromLangiumDiagnostic` inference table
// so codes line up across LSP-pushed rows and renderSource-derived
// rows. Keeps preview-shell filters that key on `code` consistent
// regardless of which pipeline produced the diagnostic.
function inferCodeFromMessage(message: string): string {
    const lower = message.toLowerCase();
    if (lower.includes('duplicate identifier')) return 'duplicate-identifier';
    if (lower.includes('unknown reference') || lower.includes('did you mean'))
        return 'unknown-reference';
    if (lower.includes('circular')) return 'circular-dependency';
    if (lower.includes('requires') && lower.includes('date:')) return 'missing-date';
    if (lower.includes('duration')) return 'duration';
    if (lower.includes('include')) return 'include';
    if (lower.includes('indent')) return 'indentation';
    return 'validation';
}

function extractSuggestion(message: string): string | undefined {
    const match = message.match(DID_YOU_MEAN_RE);
    return match?.[1]?.trim();
}
